'use client'

import { useEffect, useMemo, useRef } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { readDraft, writeDraft } from '@/features/consignment-form/draft'
import { useEstimates } from '@/features/consignment-form/hooks/use-estimates'
import { isItemField, useItemEditor } from '@/features/consignment-form/hooks/use-item-editor'
import { useItemPricing } from '@/features/consignment-form/hooks/use-item-pricing'
import { useSubmission } from '@/features/consignment-form/hooks/use-submission'
import type { Catalogue } from '@/features/consignment-form/contract'
import { errorSummary, formatBackendError } from '@/features/consignment-form/labels'
import type { Consignment, Item } from '@/features/consignment-form/schema'
import { emptyConsignment, submittableSchema } from '@/features/consignment-form/schema'
import { ItemsSection } from '@/features/consignment-form/sections/ItemsSection'
import { PaymentSection } from '@/features/consignment-form/sections/PaymentSection'
import { PartySection } from '@/features/consignment-form/sections/PartySection'
import { ShipmentSection } from '@/features/consignment-form/sections/ShipmentSection'
import { SubmitBar } from '@/features/consignment-form/sections/SubmitBar'
import { Banner } from '@/ui/Banner'
import { TextLink } from '@/ui/TextLink'
import { ErrorList } from '@/ui/ErrorList'
import { t } from '@/lang'
import { testIds } from '@/lib/test-ids'

/** Guest view of the consignment note. */
export function ConsignmentForm({ catalogue }: { catalogue: Catalogue }) {
  const { products } = catalogue
  const catalogueError =
    catalogue.products === null && catalogue.error ? formatBackendError(catalogue.error) : undefined
  // Rows are held against the loaded catalogue at Submit, so the resolver follows the catalogue.
  const resolver = useMemo(() => zodResolver(submittableSchema(products ?? [])), [products])
  const form = useForm<Consignment>({
    // Validate a field when it is first left, and on every change after that.
    mode: 'onTouched',
    shouldFocusError: false,
    defaultValues: emptyConsignment,
    resolver,
  })
  const { control, formState, handleSubmit, reset, subscribe } = form
  const items = useFieldArray({ control, name: 'items' })
  const editor = useItemEditor(form, items)
  const { restore: restoreEditor } = editor
  const estimates = useEstimates(products, items.fields)
  const pricing = useItemPricing({ estimates, editor, rows: items.fields })
  const submission = useSubmission({ onCreated: editor.discard })
  const summaryRef = useRef<HTMLDivElement>(null)
  const messageRef = useRef<HTMLDivElement>(null)
  const estimateRef = useRef<HTMLDivElement>(null)
  const summary = formState.submitCount > 0 ? errorSummary(formState.errors) : []

  useEffect(() => {
    if (formState.submitCount > 0) summaryRef.current?.focus()
  }, [formState.submitCount])

  useEffect(() => {
    if (submission.notice > 0) messageRef.current?.focus()
  }, [submission.notice])

  useEffect(() => {
    if (pricing.notice > 0) estimateRef.current?.focus()
  }, [pricing.notice])

  /* oxlint-disable react/set-state-in-effect -- Browser-only drafts must be restored after hydration. */
  useEffect(() => {
    const draft = readDraft()
    if (draft) reset(draft, { keepDefaultValues: true })
    restoreEditor(draft?.items)
  }, [reset, restoreEditor])
  /* oxlint-enable react/set-state-in-effect */

  useEffect(() => subscribe({ formState: { values: true }, callback: ({ values }) => writeDraft(values) }), [subscribe])

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (submission.status !== 'success' && (formState.isDirty || editor.dirty.current)) event.preventDefault()
    }

    globalThis.addEventListener('beforeunload', warn)
    return () => globalThis.removeEventListener('beforeunload', warn)
  }, [formState.isDirty, submission.status, editor.dirty])

  const openInvalidItem = (index: number, field: keyof Item) => {
    editor.openAt(index, field)
    submission.warn(t('form.check-item', { number: index + 1 }))
  }

  const submit = (values: Consignment) => {
    if (pricing.holdsSubmit()) return
    if (products) submission.submit(values, products)
  }

  const invalidSubmit = (errors: typeof formState.errors) => {
    const itemErrors: unknown = errors.items
    if (!Array.isArray(itemErrors)) return
    const index = itemErrors.findIndex(Boolean)
    if (index < 0) return
    const first: unknown = itemErrors[index]
    const field = typeof first === 'object' && first !== null ? Object.keys(first).find(isItemField) : undefined
    openInvalidItem(index, field ?? 'category')
  }

  if (submission.status === 'success') {
    const [numberBefore, numberAfter] = t('success.number').split('{number}')
    return (
      <div className="pt-10" data-testid={testIds.consignmentForm.root}>
        <div aria-live="polite" aria-atomic="true">
          <h1 className="text-h1 text-primary-600">{t('success.title')}</h1>
          <p className="mt-5 text-sm text-neutral-800">
            {numberBefore}
            <strong className="font-bold text-primary-600">{submission.createdNumber}</strong>
            {numberAfter}
          </p>
          <p className="mt-2 text-sm text-neutral-800">{t('success.next-steps')}</p>
        </div>
        <TextLink href="/" className="mt-5">
          {t('success.create-another')}
        </TextLink>
      </div>
    )
  }

  return (
    <form
      data-testid={testIds.consignmentForm.root}
      className="space-y-7.5"
      onSubmit={(event) => void handleSubmit(submit, invalidSubmit)(event)}
    >
      <div className="pt-10 pb-2.5">
        <h1 className="text-h1 text-primary-600">{t('form.title')}</h1>
        <p className="mt-5 text-sm font-medium text-neutral-800">{t('form.intro')}</p>
      </div>

      {products ? null : (
        <Banner variant="error">
          <div role="alert">
            <p>{t('form.catalogue-error')}</p>
            {catalogueError ? <ErrorList heading={catalogueError.headline} entries={catalogueError.lines} /> : null}
            <p className="mt-1">{t('form.catalogue-error-advice')}</p>
          </div>
        </Banner>
      )}

      {summary.length > 0 ? (
        <Banner variant="error" tabIndex={-1} ref={summaryRef} data-testid={testIds.consignmentForm.errorSummary}>
          <p>{t('form.error-summary')}</p>
          <ErrorList entries={summary.map(({ label, message }) => `${label}: ${message}`)} />
        </Banner>
      ) : null}

      {submission.message ? (
        <Banner variant="error" tabIndex={-1} ref={messageRef}>
          <div role="alert">
            <p>{submission.message}</p>
            {submission.details ? (
              <ErrorList heading={submission.details.headline} entries={submission.details.lines} />
            ) : null}
          </div>
        </Banner>
      ) : null}
      {submission.status === 'unknown' ? <Banner variant="warning">{t('form.result-unknown')}</Banner> : null}

      {pricing.failures.length > 0 ? (
        <Banner variant="error" tabIndex={-1} ref={estimateRef}>
          <div role="alert">
            <p>{t('form.estimate-error')}</p>
            <ErrorList
              entries={pricing.failures.map(({ label, headline, lines }) => ({
                text: `${label} — ${headline}`,
                lines,
              }))}
            />
          </div>
        </Banner>
      ) : null}

      <fieldset disabled={submission.status !== 'idle'} className="min-w-0 space-y-7.5 border-0 p-0">
        <PartySection form={form} party="shipper" />

        <ShipmentSection form={form} />

        <PaymentSection form={form} />

        <PartySection form={form} party="consignee" />

        <ItemsSection
          products={products ?? []}
          items={items.fields}
          estimates={estimates.estimates}
          editingIndex={editor.index}
          adding={editor.adding}
          draftItem={editor.draftItem}
          onEdit={editor.open}
          onAdd={editor.add}
          onSave={pricing.save}
          onDraftChange={editor.change}
          onDelete={pricing.remove}
          onCancel={pricing.close}
        />
      </fieldset>

      {/* Outside the fieldset: after an unknown result the fields lock, but Retry must stay pressable. */}
      <SubmitBar
        submittable={
          products !== null &&
          editor.index === null &&
          !editor.adding &&
          estimates.status !== 'loading' &&
          (submission.status === 'idle' || submission.status === 'unknown')
        }
        submitting={submission.status === 'submitting'}
        onRetry={submission.status === 'unknown' ? submission.retry : undefined}
        estimateStatus={estimates.status}
        estimatedTotal={estimates.total}
      />
    </form>
  )
}
